import {
  AiRejectedDocumentItem,
  IDDocumentType,
  VerificationType,
} from "@amzn/client-workbench-api-model";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { H4, Text } from "@amzn/stencil-react-components/text";
import IconCheckCircleFillLarge from "@amzn/stencil-react-icons/icons/icon-check-circle-fill-large";
import IconCloseCircleFillLarge from "@amzn/stencil-react-icons/icons/icon-close-circle-fill-large";
import React, { useContext, useState } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";

import idvQualityBlurryUpload from "@/assets/images/idvQualityBlurryUpload.png";
import idvQualityClearUpload from "@/assets/images/idvQualityClearUpload.png";
import idvQualityWebcamCover from "@/assets/images/idvQualityWebcamCover.png";
import idvQualityWindowsLaptop from "@/assets/images/idvQualityWindowsLaptop.png";
import FullIdVerification from "@/components/IdentityCheck/FullIdVerification";
import {
  DOCS_SUBMITTED_KEY,
  getAiRejectedDocuments,
  setAiRetryContext,
} from "@/components/IdentityCheck/helper/rivHelper";
import LightweightIdVerification from "@/components/IdentityCheck/LightweightIdVerification";
import { useUserProgress } from "@/hooks/useUserProgress";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { Day1EventCategory } from "@/types/snowplow-events";

/**
 * Surface the AI quality gate's per-artifact rejection details
 * and let the new hire request fresh presigned URLs for the flagged
 * artifacts only. After the user uploads the retry, the AI gate is bypassed
 * and the bundle proceeds straight to OCR + the human verifier (regardless
 * of the retry's quality), so the wording here emphasises that this is a
 * one-shot opportunity to improve the submission.
 */
export default function AiQualityRejected(): JSX.Element {
  const { dispatch } = useContext(AppContext);
  const { data: userProgress } = useUserProgress();
  const [retryReady, setRetryReady] = useState(false);
  const intl = useIntl();

  const aiRejectedDocs: AiRejectedDocumentItem[] = userProgress
    ? getAiRejectedDocuments(userProgress.verificationStatus)
    : [];

  const verificationType: VerificationType = (() => {
    // Derive from the progress data rather than docsVerificationStatus, which
    // is AI_REJECTED at this point and doesn't indicate light vs full.
    if (userProgress) {
      const lightProgress = userProgress.verificationStatus.find(
        (p) => p.VerificationType === "ON_BOARDING_LIGHT_VERIFICATION"
      );
      const fullProgress = userProgress.verificationStatus.find(
        (p) => p.VerificationType === "ON_BOARDING_FULL_VERIFICATION"
      );
      const isLight =
        lightProgress &&
        lightProgress.VerificationTypeStatus !== "NOT_APPLICABLE" &&
        fullProgress?.VerificationTypeStatus === "NOT_APPLICABLE";
      if (isLight) {
        return "ON_BOARDING_LIGHT_VERIFICATION";
      }
    }
    return "ON_BOARDING_FULL_VERIFICATION";
  })();

  const handleRetry = (): void => {
    if (aiRejectedDocs.length === 0) {
      return;
    }
    const docTypes: IDDocumentType[] = aiRejectedDocs.map(
      (item) => item.documentType
    );

    setAiRetryContext({ verificationType, documentTypes: docTypes });

    localStorage.removeItem(DOCS_SUBMITTED_KEY);

    dispatch({
      type: ActionType.REMOVE_DOCUMENTS_BY_TYPES,
      removeDocumentTypes: docTypes as string[],
    });

    setRetryReady(true);
  };

  if (!userProgress) {
    return <Spinner />;
  }

  // Once retry presigned URLs are cached, render the recording component
  // directly. We bypass the IdentityCheck switch because isManualIdvInFlight
  // would route to VerificationPending instead of the recording UI.
  if (retryReady) {
    if (verificationType === "ON_BOARDING_LIGHT_VERIFICATION") {
      return (
        <LightweightIdVerification
          eventCategory={Day1EventCategory.IDCheckLight}
        />
      );
    }
    return <FullIdVerification allowAutomatedIdvFallback={false} />;
  }

  // Ensure primary ID documents always appear before secondary.
  const sortedDocs = [...aiRejectedDocs].sort((a, b) => {
    const priority = (docType: IDDocumentType): number => {
      switch (docType) {
        case "PRIMARY_ID":
        case "PRIMARY_ID_RECORDING":
          return 0;
        case "SECONDARY_ID":
        case "SECONDARY_ID_RECORDING":
          return 1;
        default:
          return 2;
      }
    };
    return priority(a.documentType) - priority(b.documentType);
  });

  return (
    <Col gridGap="S400" dataTestId="ai-quality-rejected">
      <H4>
        <FormattedMessage id="identityCheck-aiQualityRejected-heading" />
      </H4>
      <Col gridGap="S400">
        {sortedDocs.map((doc) => (
          <Col
            key={doc.documentType}
            gridGap="S200"
            dataTestId={`ai-quality-rejected-item-${doc.documentType}`}
          >
            <Text fontWeight="bold">
              {humanizeDocumentType(doc.documentType, intl)}:
            </Text>
            <MessageBanner type={MessageBannerType.Error} isDismissible={true}>
              <Text>{summarizeFailureReasons(doc.failureReasons, intl)}</Text>
            </MessageBanner>
            {renderExampleImages(doc.failureReasons, intl)}
          </Col>
        ))}
      </Col>
      <Row gridGap="S300">
        <Button
          variant={ButtonVariant.Primary}
          onClick={(): void => {
            handleRetry();
          }}
          disabled={aiRejectedDocs.length === 0}
          dataTestId="ai-quality-retry-button"
        >
          <FormattedMessage id="identityCheck-aiQualityRejected-button-reRecord" />
        </Button>
      </Row>
    </Col>
  );
}

/**
 * Render do/don't example images below the error banner when the failure
 * reasons match a category that has illustrative guidance.
 */
function renderExampleImages(
  reasons: string[],
  intl: IntlShape
): JSX.Element | null {
  if (reasons.includes("LOW_QUALITY_CAMERA_BLOCKED")) {
    return (
      <Row
        gridGap="S400"
        padding="S400"
        backgroundColor="color.surface.bg-muted"
        justifyContent="center"
        alignItems="flex-start"
        dataTestId="ai-quality-example-camera-blocked"
      >
        <Col alignItems="center" gridGap="S200">
          <IconCheckCircleFillLarge
            color="color.status.success-icon"
            aria-hidden
          />
          <img
            src={idvQualityWindowsLaptop}
            alt={intl.formatMessage({
              id: "identityCheck-aiQualityRejected-image-webcamUncovered-altText",
            })}
            width={244}
            height={174}
          />
        </Col>
        <Col alignItems="center" gridGap="S200">
          <IconCloseCircleFillLarge
            color="color.status.critical-icon"
            aria-hidden
          />
          <img
            src={idvQualityWebcamCover}
            alt={intl.formatMessage({
              id: "identityCheck-aiQualityRejected-image-webcamCovered-altText",
            })}
            width={244}
            height={174}
          />
        </Col>
      </Row>
    );
  }

  // Return null for higher-priority reasons that lack illustrative guidance,
  // so we don't show misleading blurry/clear images when the text message
  // is about a different issue (e.g. "show the front of your ID").
  if (
    reasons.includes("LOW_QUALITY_BLANK") ||
    reasons.includes("LOW_QUALITY_WRONG_SIDE")
  ) {
    return null;
  }

  if (
    reasons.includes("LOW_QUALITY_BLURRY") ||
    reasons.includes("LOW_QUALITY_OUT_OF_FOCUS") ||
    reasons.includes("LOW_QUALITY_GLARE") ||
    reasons.includes("LOW_QUALITY_OBSCURED")
  ) {
    return (
      <Row
        gridGap="S400"
        padding="S400"
        backgroundColor="color.surface.bg-muted"
        justifyContent="center"
        alignItems="flex-start"
        dataTestId="ai-quality-example-blurry"
      >
        <Col alignItems="center" gridGap="S200">
          <IconCheckCircleFillLarge
            color="color.status.success-icon"
            aria-hidden
          />
          <img
            src={idvQualityClearUpload}
            alt={intl.formatMessage({
              id: "identityCheck-aiQualityRejected-image-clearUpload-altText",
            })}
            width={244}
            height={174}
          />
        </Col>
        <Col alignItems="center" gridGap="S200">
          <IconCloseCircleFillLarge
            color="color.status.critical-icon"
            aria-hidden
          />
          <img
            src={idvQualityBlurryUpload}
            alt={intl.formatMessage({
              id: "identityCheck-aiQualityRejected-image-blurryUpload-altText",
            })}
            width={244}
            height={174}
          />
        </Col>
      </Row>
    );
  }

  return null;
}

/**
 * Map an ``IDDocumentType`` to localized human-readable text for the
 * rejection list. Unknown document types are rendered as-is.
 */
function humanizeDocumentType(
  documentType: IDDocumentType,
  intl: IntlShape
): string {
  switch (documentType) {
    case "PRIMARY_ID":
      return intl.formatMessage({
        id: "identityCheck-aiQualityRejected-docType-primaryIdPhoto",
      });
    case "PRIMARY_ID_RECORDING":
      return intl.formatMessage({
        id: "identityCheck-aiQualityRejected-docType-primaryIdRecording",
      });
    case "SECONDARY_ID":
      return intl.formatMessage({
        id: "identityCheck-aiQualityRejected-docType-secondaryIdPhoto",
      });
    case "SECONDARY_ID_RECORDING":
      return intl.formatMessage({
        id: "identityCheck-aiQualityRejected-docType-secondaryIdRecording",
      });
    case "LIVENESS_RECORDING":
      return intl.formatMessage({
        id: "identityCheck-aiQualityRejected-docType-livenessRecording",
      });
    default:
      return documentType;
  }
}

/**
 * Summarise the AI quality agent's failure reasons in localized
 * user-facing text.
 */
function summarizeFailureReasons(reasons: string[], intl: IntlShape): string {
  // Priority-ordered: return the first matching message. Each condition
  // maps to a distinct Figma screen so we show one clear instruction
  // rather than combining multiple potentially confusing snippets.
  if (reasons.includes("LOW_QUALITY_CAMERA_BLOCKED")) {
    return intl.formatMessage({
      id: "identityCheck-aiQualityRejected-reason-cameraBlocked",
    });
  }
  if (reasons.includes("LOW_QUALITY_BLANK")) {
    return intl.formatMessage({
      id: "identityCheck-aiQualityRejected-reason-blank",
    });
  }
  if (reasons.includes("LOW_QUALITY_WRONG_SIDE")) {
    return intl.formatMessage({
      id: "identityCheck-aiQualityRejected-reason-wrongSide",
    });
  }
  if (
    reasons.includes("LOW_QUALITY_GLARE") ||
    reasons.includes("LOW_QUALITY_OBSCURED")
  ) {
    return intl.formatMessage({
      id: "identityCheck-aiQualityRejected-reason-glareObscured",
    });
  }

  // Generic fallback for other quality issues (blurry, out of focus,
  // over/under exposed, filter applied, etc.) and for empty reason lists.
  return intl.formatMessage({
    id: "identityCheck-aiQualityRejected-reason-generic",
  });
}
